import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Skeleton } from "@/shared/ui/skeleton";
import { CheckCircle2, XCircle, Clock, Eye, CalendarDays, User, Search } from "lucide-react";
import { format } from "date-fns";
import { fetchRunsByFactory } from "@/modules/checklist/api";
import { RunDetailSheet } from "./RunDetailSheet";
import type { ChecklistStatus } from "@/modules/checklist/api/types";

interface CompletionsTabProps {
  tenantId: string;
  factoryId: string;
  /** When provided, restricts the list to runs for this template only. */
  templateId?: string;
}

function StatusBadge({ status }: { status: string }) {
  if (status === "completed") {
    return (
      <Badge variant="default" className="gap-1 bg-green-600 hover:bg-green-600 text-xs">
        <CheckCircle2 className="w-3 h-3" />
        Completed
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge variant="destructive" className="gap-1 text-xs">
        <XCircle className="w-3 h-3" />
        Failed
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="gap-1 text-xs">
      <Clock className="w-3 h-3" />
      In Progress
    </Badge>
  );
}

export function CompletionsTab({ tenantId, factoryId, templateId }: CompletionsTabProps) {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [operatorSearch, setOperatorSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { data: runs = [], isLoading } = useQuery({
    queryKey: ["core", "checklist-runs", tenantId, factoryId, templateId, statusFilter, dateFrom, dateTo],
    queryFn: () =>
      fetchRunsByFactory(tenantId, factoryId, {
        status: statusFilter !== "all" ? (statusFilter as ChecklistStatus) : undefined,
        templateId,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      }),
    enabled: !!tenantId && !!factoryId,
  });

  // Client-side operator name filter
  const filtered = operatorSearch.trim()
    ? runs.filter((r) => {
        const name = r.operator?.full_name ?? r.operator?.email ?? "";
        return name.toLowerCase().includes(operatorSearch.toLowerCase());
      })
    : runs;

  return (
    <>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 pb-4">
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search operator..."
            value={operatorSearch}
            onChange={(e) => setOperatorSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-[130px] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-8 w-[130px] text-sm"
          />
          <span className="text-muted-foreground text-xs">–</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-8 w-[130px] text-sm"
          />
        </div>
        {(statusFilter !== "all" || operatorSearch || dateFrom || dateTo) && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-muted-foreground"
            onClick={() => {
              setStatusFilter("all");
              setOperatorSearch("");
              setDateFrom("");
              setDateTo("");
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          <Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm font-medium">No completions found</p>
          <p className="text-xs mt-1">
            {runs.length > 0 ? "Try adjusting the filters" : "No runs have been recorded yet"}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Header row */}
          <div className="hidden md:grid grid-cols-[1fr_auto_auto_auto_auto] gap-3 px-3 pb-1 text-xs text-muted-foreground font-medium uppercase tracking-wide">
            <span>Operator</span>
            {!templateId && <span>Template</span>}
            <span>Shift date</span>
            <span>Status</span>
            <span />
          </div>
          {filtered.map((run) => {
            const operatorDisplay = run.operator
              ? run.operator.full_name || run.operator.email
              : "Unknown operator";

            return (
              <div
                key={run.id}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-muted/30 transition-colors"
              >
                <div className="flex-1 min-w-0 space-y-0.5">
                  <div className="flex items-center gap-2">
                    <User className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm font-medium truncate">{operatorDisplay}</span>
                  </div>
                  {!templateId && run.template && (
                    <p className="text-xs text-muted-foreground pl-5 truncate">{run.template.name}</p>
                  )}
                </div>

                <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <CalendarDays className="w-3.5 h-3.5" />
                    {format(new Date(run.shift_date), "MMM d, yyyy")}
                  </div>
                  <StatusBadge status={run.status} />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1.5 text-xs ml-auto sm:ml-0"
                    onClick={() => setSelectedRunId(run.id)}
                  >
                    <Eye className="w-3.5 h-3.5" />
                    View
                  </Button>
                </div>
              </div>
            );
          })}
          <p className="text-xs text-muted-foreground text-right pt-1">
            {filtered.length} {filtered.length === 1 ? "run" : "runs"} shown
          </p>
        </div>
      )}

      <RunDetailSheet
        tenantId={tenantId}
        runId={selectedRunId}
        open={!!selectedRunId}
        onClose={() => setSelectedRunId(null)}
      />
    </>
  );
}
